import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { fetchFoodLogs, fetchClientDailyTargets } from '../../services/clientApi';
import { logTotals, logDay } from '../../utils/foodLogs';

const CHART_ANIM = { duration: 2200, easing: 'ease-out', begin: 400 };

function FoodLogProgressComponent({ userCode, clientId, themeClasses, language, isDarkMode }) {
  const [foodLogs, setFoodLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('calories');
  const [timePeriod, setTimePeriod] = useState('all');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAverage, setShowAverage] = useState(false);
  // The daily targets, drawn as a reference line when one metric is selected.
  const [targets, setTargets] = useState(null);

  useEffect(() => {
    const loadFoodLogs = async () => {
      if (!userCode) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await fetchFoodLogs(userCode);
      
      if (error) {
        console.error('Error loading food logs:', error);
        setFoodLogs([]);
      } else {
        setFoodLogs(data || []);
      }
      setLoading(false);
    };

    loadFoodLogs();
  }, [userCode]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    fetchClientDailyTargets(clientId)
      .then((t) => {
        if (cancelled || !t) return;
        setTargets({
          calories: parseFloat(t.calories) || 0,
          protein: parseFloat(t.protein) || 0,
          carbs: parseFloat(t.carbs) || 0,
          fat: parseFloat(t.fats ?? t.fat) || 0,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), 400);
    return () => clearTimeout(timer);
  }, [timePeriod, selectedMetric]);

  // Format date for display (short format)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'hebrew' 
      ? date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })
      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format date for tooltip (full format)
  const formatDateFull = (dateString) => {
    const date = new Date(dateString);
    return language === 'hebrew'
      ? date.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' })
      : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Aggregate food logs by date
  const aggregateByDate = (logs) => {
    const dailyTotals = {};
    logs.forEach((log) => {
      const date = logDay(log);
      if (!date) return;
      if (!dailyTotals[date]) dailyTotals[date] = { date, calories: 0, protein: 0, carbs: 0, fat: 0 };
      const totals = logTotals(log);
      dailyTotals[date].calories += totals.calories;
      dailyTotals[date].protein += totals.protein;
      dailyTotals[date].carbs += totals.carbs;
      dailyTotals[date].fat += totals.fat;
    });
    return Object.values(dailyTotals).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Get chart data points with time period filtering
  const getChartData = () => {
    if (!foodLogs || foodLogs.length === 0) return [];

    const aggregated = aggregateByDate(foodLogs);
    let filteredData = [...aggregated];

    // Filter by time period
    if (timePeriod !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (timePeriod) {
        case '1m':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case '3m':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case '6m':
          cutoffDate.setMonth(now.getMonth() - 6);
          break;
        default:
          break;
      }
      
      filteredData = aggregated.filter(d => {
        const logDate = new Date(d.date);
        return logDate >= cutoffDate;
      });
    }

    return filteredData.map(d => ({
      date: formatDate(d.date),
      dateFull: d.date,
      calories: d.calories,
      protein: d.protein,
      carbs: d.carbs,
      fat: d.fat
    }));
  };

  const chartData = getChartData();

  const getMetricValue = (d) => d[selectedMetric];

  const getCurrentValue = () => {
    if (chartData.length === 0) return null;
    return getMetricValue(chartData[chartData.length - 1]);
  };

  // Get metric unit
  const getMetricUnit = () => {
    switch(selectedMetric) {
      case 'calories': return 'kcal';
      case 'protein':
      case 'carbs':
      case 'fat': return 'g';
      default: return 'kcal';
    }
  };

  // Get metric label
  const getMetricLabel = () => {
    switch(selectedMetric) {
      case 'calories': return language === 'hebrew' ? 'קלוריות' : 'Calories';
      case 'protein': return language === 'hebrew' ? 'חלבון' : 'Protein';
      case 'carbs': return language === 'hebrew' ? 'פחמימות' : 'Carbs';
      case 'fat': return language === 'hebrew' ? 'שומן' : 'Fat';
      default: return language === 'hebrew' ? 'קלוריות' : 'Calories';
    }
  };

  // One calm line colour for every metric: the labels and units tell them apart.
  const getMetricColor = () => 'var(--forest-ondark)';

  const currentValue = getCurrentValue();
  const metricColor = getMetricColor();

  // Calculate average value for selected metric
  const getAverageValue = () => {
    if (chartData.length === 0) return null;
    const values = chartData.map(d => getMetricValue(d)).filter(v => v != null && v > 0);
    if (values.length === 0) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  };

  const averageValue = getAverageValue();
  const targetValue = targets ? targets[selectedMetric] || 0 : 0;

  if (loading) {
    return (
      <div className={`${themeClasses.bgCard} rounded-lg p-6 mb-6 animate-pulse`}>
        <div className="h-48 bg-moss rounded-lg"></div>
      </div>
    );
  }

  const hasData = chartData.length > 0;

  return (
    <div className={`${themeClasses.bgCard} rounded-lg p-3 sm:p-4 md:p-6 mb-6  border ${isDarkMode ? 'border-line' : 'border-line'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className={`${themeClasses.textPrimary} text-lg sm:text-xl font-bold`}>
            {language === 'hebrew' ? 'מעקב תזונה' : 'Nutrition Tracking'}
          </h3>
          <div className="flex flex-wrap gap-2 sm:gap-4 mt-1">
            {currentValue != null && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'ערך נוכחי: ' : 'Current: '}
                <span className="font-semibold">
                  {Math.round(currentValue)} {getMetricUnit()}
                </span>
              </p>
            )}
            {chartData.length > 0 && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'ימים שתועדו: ' : 'Days logged: '}
                <span className="font-semibold">{chartData.length}</span>
              </p>
            )}
            {targetValue > 0 && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'יעד: ' : 'Target: '}
                <span className="font-semibold">{Math.round(targetValue)} {getMetricUnit()}</span>
              </p>
            )}
            {averageValue != null && (
              <p className={`${themeClasses.textSecondary} text-xs sm:text-sm whitespace-nowrap`}>
                {language === 'hebrew' ? 'ממוצע: ' : 'Average: '}
                <span className="font-semibold">
                  {Math.round(averageValue)} {getMetricUnit()}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Time Period Filter */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 items-center">
        {[
          { key: '1m', label: language === 'hebrew' ? 'חודש 1' : '1 Month' },
          { key: '3m', label: language === 'hebrew' ? '3 חודשים' : '3 Months' },
          { key: '6m', label: language === 'hebrew' ? '6 חודשים' : '6 Months' },
          { key: 'all', label: language === 'hebrew' ? 'כל הזמן' : 'All Time' }
        ].map(period => (
          <button
            key={period.key}
            onClick={() => setTimePeriod(period.key)}
            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              timePeriod === period.key
                ? 'bg-forest-ondark text-white dark:text-canvas-dark'
                : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:${themeClasses.bgPrimary}`
            }`}
          >
            {period.label}
          </button>
        ))}
        <button
          onClick={() => setShowAverage(!showAverage)}
          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
            showAverage
              ? 'bg-forest-ondark text-white dark:text-canvas-dark  '
              : `${themeClasses.bgSecondary} ${themeClasses.textSecondary} hover:${themeClasses.bgPrimary}`
          }`}
        >
          {language === 'hebrew' ? 'ממוצע' : 'Average'}
        </button>
      </div>

      {/* Metric Selector */}
      <div className="mb-3 sm:mb-4 flex flex-wrap gap-1.5 sm:gap-2">
        {[
          { key: 'calories', label: language === 'hebrew' ? 'קלוריות' : 'Calories' },
          { key: 'protein', label: language === 'hebrew' ? 'חלבון' : 'Protein' },
          { key: 'carbs', label: language === 'hebrew' ? 'פחמימות' : 'Carbs' },
          { key: 'fat', label: language === 'hebrew' ? 'שומן' : 'Fat' }
        ].map(metric => (
          <button
            key={metric.key}
            onClick={() => setSelectedMetric(metric.key)}
            aria-pressed={selectedMetric === metric.key}
            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              selectedMetric === metric.key
                ? 'bg-forest-ondark text-white dark:text-canvas-dark'
                : `${themeClasses.bgSecondary} ${themeClasses.textSecondary}`
            }`}
          >
            {metric.label}
          </button>
        ))}
      </div>

      {/* Recharts line chart (theme-aware) */}
      {(
        <div className={`rounded-lg border p-4  ${isDarkMode ? 'border-line bg-surface/80' : 'border-line bg-surface'}`}>
          {!hasData ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-4xl mb-3" aria-hidden="true">📊</div>
              <p className={`${themeClasses.textSecondary} text-sm sm:text-base text-center px-4`}>
                {language === 'hebrew' ? 'הלקוח עדיין לא תיעד ארוחות, אז אין מה להציג בגרף.' : "This client hasn't logged any meals yet, so there is nothing to chart."}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 12, bottom: 24 }}>
                <defs>
                  <linearGradient id="gradFoodLog" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={metricColor} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={metricColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="date" reversed={language === 'hebrew'} tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} />
                <YAxis orientation={language === 'hebrew' ? 'right' : 'left'} tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'var(--ink)' }}
                  formatter={(value) => [value != null ? Math.round(value) + ' ' + getMetricUnit() : '', getMetricLabel()]}
                  labelFormatter={(label) => {
                    const d = chartData.find(c => c.date === label);
                    return d ? formatDateFull(d.dateFull) : label;
                  }}
                />
                <Legend wrapperStyle={{ color: 'var(--ink-muted)' }} />
                {targetValue > 0 && (
                  <ReferenceLine
                    y={targetValue}
                    stroke="var(--ink-muted)"
                    strokeDasharray="2 4"
                    label={{ value: language === 'hebrew' ? 'יעד' : 'Target', position: language === 'hebrew' ? 'left' : 'right', fill: 'var(--ink-muted)', fontSize: 11 }}
                  />
                )}
                {showAverage && averageValue != null && (
                  <ReferenceLine
                    y={averageValue}
                    stroke={metricColor}
                    strokeDasharray="6 4"
                    strokeOpacity={0.7}
                    label={{ value: language === 'hebrew' ? 'ממוצע' : 'Avg', position: language === 'hebrew' ? 'left' : 'right', fill: metricColor }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey={selectedMetric}
                  name={getMetricLabel()}
                  stroke={metricColor}
                  fill="url(#gradFoodLog)"
                  strokeWidth={2}
                  dot={{ fill: metricColor, strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2, fill: metricColor }}
                  isAnimationActive={true}
                  animationDuration={CHART_ANIM.duration}
                  animationEasing={CHART_ANIM.easing}
                  animationBegin={CHART_ANIM.begin}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}

export default FoodLogProgressComponent;
